// frontend/src/hooks/useCalendarColors.js
import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Paleta de colores para pacientes (colores contrastantes y agradables)
const PATIENT_COLORS = [
  '#FF6B6B', // Rojo coral
  '#4ECDC4', // Turquesa
  '#45B7D1', // Azul cielo
  '#96CEB4', // Verde menta
  '#FFEAA7', // Amarillo suave
  '#DDA0DD', // Ciruela
  '#98D8C8', // Verde agua
  '#F7DC6F', // Amarillo dorado
  '#BB8FCE', // Lavanda
  '#85C1E9', // Azul claro
  '#F8C471', // Naranja suave
  '#82E0AA', // Verde claro
  '#F1948A', // Rosa salmón
  '#85C1E9', // Azul bebé
  '#D2B4DE', // Morado claro
  '#A9DFBF', // Verde pastel
  '#FAD7A0', // Durazno
  '#AED6F1', // Azul pastel
  '#F9E79F', // Amarillo pastel
  '#D7BDE2'  // Lila
];

// Función para generar color consistente basado en ID
const generatePatientColor = (patientId) => {
  if (!patientId) return '#6c757d'; // Gris por defecto
  
  // Usar el ID del paciente para obtener un índice consistente
  const colorIndex = patientId % PATIENT_COLORS.length;
  return PATIENT_COLORS[colorIndex];
};

// Función para obtener color por tipo de etapa de tratamiento
const getEtapaColor = (cita) => {
 
  // Obtener nombre de etapa de diferentes fuentes
  const etapaNombre = cita.etapaNombre || 
                     cita.extendedProps?.etapaNombre || 
                     cita.etapa?.nombre || 
                     '';
                     
 
  if (etapaNombre) {
    const etapaUpper = etapaNombre.toUpperCase();
    
    // Consulta inicial - NARANJA
    if (etapaUpper.includes('CONSULTA')) {
       return '#FF9800';
    }
    
    // Evaluación - FUCSIA OSCURO
    if (etapaUpper.includes('EVALUACIÓN') || etapaUpper.includes('EVALUACION')) {
       return '#AD1457';
    }
    
    // Informe - VERDE
    if (etapaUpper.includes('INFORME')) {
       return '#4CAF50';
    }
    
    // Actividades grupales - AMARILLO
    if (etapaUpper.includes('GRUPO') || etapaUpper.includes('GRUPAL') || 
        etapaUpper.includes('TALLER') || etapaUpper.includes('TALLERES')) {
       return '#FFC107';
    }
    
    // Terapia - TURQUESA OSCURO
    if (etapaUpper.includes('TERAPIA') || etapaUpper.includes('SESION') || 
        etapaUpper.includes('SEGUIMIENTO') || etapaUpper.includes('INDIVIDUALES')) {
       return '#009C96';
    }
  }
  
  // Si tiene grupo_id, es cita grupal - AMARILLO
  if (cita.grupo_id || cita.extendedProps?.grupo_id) {
     return '#FFC107';
  }
  
  // Otros tipos - AZUL
   return '#1976D2';
};
 
// Función para obtener color de especialidad
const getEspecialidadColor = (cita) => {
  // Si la cita ya tiene backgroundColor (viene del backend)
  if (cita.backgroundColor) {
    return cita.backgroundColor;
  }
  
  // Si la cita tiene especialidad con color definido
  if (cita.especialidad?.color) {
    return cita.especialidad.color;
  }
  
  // Si viene de extendedProps (formato FullCalendar)
  if (cita.extendedProps?.especialidad?.color) {
    return cita.extendedProps.especialidad.color;
  }
  
  // Si viene en extendedProps.backgroundColor
  if (cita.extendedProps?.backgroundColor) {
    return cita.extendedProps.backgroundColor;
  }
  
  // Color por defecto si no encuentra especialidad
  return '#5D1390';
};

export const useCalendarColors = (selectedEspecialista, especialidades = []) => {
  const { user } = useAuth();
  
  const colorConfig = useMemo(() => {
    if (!user) {
      return {
        shouldUseSpecialtyColors: false,
        headerColor: '#f8f9fa',
        getEventColor: (cita) => getEspecialidadColor(cita)
      };
    }

    const userRole = user.rol?.nombre;
    const isAdmin = userRole === 'Administrador';
    const isAdmision = userRole === 'Admisión';
    const isEspecialista = userRole === 'Especialista';
    const isInterno = userRole === 'Interno';

    // Para Administrador y Admisión
    if (isAdmin || isAdmision) {
      // Sin filtro de especialista: usar colores por ESPECIALIDAD
      if (!selectedEspecialista) {
        return {
          shouldUseSpecialtyColors: false,
          headerColor: '#f8f9fa',
          getEventColor: (cita) => {
            return getEspecialidadColor(cita);
          }
        };
      }
      
      // Con filtro de especialista: cabecera con color de especialidad + citas por ETAPA
      const especialidad = especialidades.find(esp => esp.id === parseInt(selectedEspecialista));
      const especialidadColor = especialidad?.color || '#5D1390';
      
      return {
        shouldUseSpecialtyColors: true,
        headerColor: especialidadColor,
        headerTextColor: getContrastColor(especialidadColor),
        getEventColor: (cita) => {
           
          // Si es evento personal, usar color gris
          if (cita.es_evento_personal) {
            return '#6d6d6d';
          }
          
          // NUEVA LÓGICA: Colores por etapa de tratamiento
          const color = getEtapaColor(cita);
           return color;
        }
      };
    }

    // Para Especialista e Interno: siempre usar color de su especialidad
    if (isEspecialista || isInterno) {
      // Obtener la especialidad del usuario
      const userEspecialidad = user.especialista?.especialidades?.[0];
      const especialidadColor = userEspecialidad?.color || '#5D1390';
      
      return {
        shouldUseSpecialtyColors: true,
        headerColor: especialidadColor,
        headerTextColor: getContrastColor(especialidadColor),
        getEventColor: (cita) => {
           
          // Si es evento personal, usar color gris
          if (cita.es_evento_personal) {
            return '#6d6d6d';
          }
          
          // NUEVA LÓGICA: Colores por etapa de tratamiento
          const color = getEtapaColor(cita);
           return color;
        }
      };
    }

    // Por defecto (otros roles) - usar colores por especialidad
    return {
      shouldUseSpecialtyColors: false,
      headerColor: '#f8f9fa',
      getEventColor: (cita) => getEspecialidadColor(cita)
    };
  }, [user, selectedEspecialista, especialidades]);

  return colorConfig;
};

// Función auxiliar para obtener color de texto contrastante
const getContrastColor = (hexColor) => {
  if (!hexColor) return '#000';
  
  // Remover # si existe
  const color = hexColor.replace('#', '');
  
  // Convertir a RGB
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  
  // Calcular luminancia
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Retornar blanco o negro según la luminancia
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

export default useCalendarColors;